import { useEffect, useState } from "react";
import { Loader2, Mail, Save, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

const btn = "inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-4 py-2 transition-colors disabled:opacity-60";

const EMPTY = { enabled: false, host: "", port: 587, security: "starttls", username: "", password: "", from_email: "", from_name: "", reply_to: "", signature_enabled: true, signature: "", signature_logo: true, bcc_enabled: false, bcc: "", password_set: false };

export function SmtpAdmin() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [testTo, setTestTo] = useState("");

  useEffect(() => {
    api.get("/admin/smtp-settings")
      .then((r) => setData({ ...EMPTY, ...r.data, password: "" }))
      .catch(() => toast.error("Nie udało się pobrać ustawień poczty."));
  }, []);

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-slate-600" /></div>;

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const payload = () => {
    const p = { ...data };
    delete p.password_set;
    if (!p.password) delete p.password; // puste = nie zmieniaj hasła
    return p;
  };

  const save = async () => {
    setBusy("save");
    try {
      const r = await api.put("/admin/smtp-settings", payload());
      setData({ ...EMPTY, ...r.data, password: "" });
      toast.success("Ustawienia konta pocztowego zapisane.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nie udało się zapisać ustawień.");
    } finally { setBusy(""); }
  };

  const test = async () => {
    setBusy("test");
    try {
      await api.put("/admin/smtp-settings", payload()); // najpierw zapisz bieżące dane
      setData((d) => ({ ...d, password: "", password_set: d.password_set || !!d.password }));
      const r = await api.post("/admin/smtp-settings/test", { to: testTo || null });
      toast.success(`Wiadomość testowa wysłana na ${r.data.to}.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Wysyłka testowa nie powiodła się.");
    } finally { setBusy(""); }
  };

  return (
    <div data-testid="smtp-admin" className="space-y-5 max-w-2xl">
      <div className="bg-card border border-border rounded-2xl p-5 sm:p-6 space-y-5">
        <div className="flex items-start gap-3">
          <Mail className="w-5 h-5 mt-0.5 text-brand-teal shrink-0" />
          <div>
            <h3 className="text-base font-semibold">Konto pocztowe (SMTP)</h3>
            <p className="text-sm text-slate-500 mt-1">
              Konto, z którego wychodzą wszystkie e-maile ze strony (potwierdzenia zamówień,
              powiadomienia, resety hasła). Dane możesz zmienić w każdej chwili.
              Gdy wysyłka jest wyłączona, obowiązuje domyślna wysyłka systemowa.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between rounded-xl border border-border px-4 py-3">
          <div>
            <div className="text-sm font-semibold">Wysyłaj e-maile przez to konto</div>
            <div className="text-xs text-slate-500">Włącz po uzupełnieniu i przetestowaniu danych.</div>
          </div>
          <Switch data-testid="smtp-enabled" checked={data.enabled} onCheckedChange={(v) => set("enabled", v)} />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Label className="text-xs">Serwer SMTP</Label>
            <Input data-testid="smtp-host" value={data.host} onChange={(e) => set("host", e.target.value)} placeholder="poczta.home.pl" />
          </div>
          <div>
            <Label className="text-xs">Port</Label>
            <Input data-testid="smtp-port" type="number" value={data.port} onChange={(e) => set("port", parseInt(e.target.value || "0", 10))} placeholder="587" />
          </div>
          <div>
            <Label className="text-xs">Szyfrowanie</Label>
            <select data-testid="smtp-security" value={data.security} onChange={(e) => set("security", e.target.value)}
              className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
              <option value="starttls">STARTTLS (port 587)</option>
              <option value="ssl">SSL/TLS (port 465)</option>
              <option value="none">Bez szyfrowania</option>
            </select>
          </div>
          <div>
            <Label className="text-xs">Login (adres skrzynki)</Label>
            <Input data-testid="smtp-username" value={data.username} onChange={(e) => set("username", e.target.value)} placeholder="info@twojebiuro.waw.pl" />
          </div>
          <div>
            <Label className="text-xs">Hasło skrzynki</Label>
            <Input data-testid="smtp-password" type="password" value={data.password} onChange={(e) => set("password", e.target.value)}
              placeholder={data.password_set ? "•••••••• (pozostaw puste, aby nie zmieniać)" : "hasło do skrzynki"} />
          </div>
          <div>
            <Label className="text-xs">Adres nadawcy (From)</Label>
            <Input data-testid="smtp-from-email" value={data.from_email} onChange={(e) => set("from_email", e.target.value)} placeholder="info@twojebiuro.waw.pl" />
            <p className="text-[11px] text-slate-400 mt-1">Zwykle taki sam jak login. Musi należeć do tej skrzynki.</p>
          </div>
          <div>
            <Label className="text-xs">Nazwa nadawcy</Label>
            <Input data-testid="smtp-from-name" value={data.from_name} onChange={(e) => set("from_name", e.target.value)} placeholder="Twoje Biuro" />
          </div>
          <div>
            <Label className="text-xs">Adres odpowiedzi (Reply-To, opcjonalny)</Label>
            <Input data-testid="smtp-reply-to" value={data.reply_to} onChange={(e) => set("reply_to", e.target.value)} placeholder="biuro@twojebiuro.waw.pl" />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 pt-1">
          <button data-testid="smtp-save-btn" onClick={save} disabled={!!busy}
            className={`${btn} bg-brand-teal text-white hover:bg-brand-tealDark`}>
            {busy === "save" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Zapisz ustawienia
          </button>
        </div>
      </div>

      <div className="bg-card border border-border rounded-2xl p-5 sm:p-6 space-y-4">
        <div>
          <h3 className="text-base font-semibold">Stopka (podpis) w e-mailach</h3>
          <p className="text-sm text-slate-500 mt-1">Dodawana na końcu każdego wychodzącego e-maila. Zostaw puste, aby użyć danych ze strony (nazwa, adres, telefon).</p>
        </div>
        <div className="flex items-center justify-between rounded-xl border border-border px-4 py-3">
          <div>
            <div className="text-sm font-semibold">Dodawaj stopkę do e-maili</div>
            <div className="text-xs text-slate-500">Nazwa firmy, adres i telefon na końcu wiadomości.</div>
          </div>
          <Switch data-testid="smtp-signature-enabled" checked={data.signature_enabled} onCheckedChange={(v) => set("signature_enabled", v)} />
        </div>
        <div className="flex items-center justify-between rounded-xl border border-border px-4 py-3">
          <div>
            <div className="text-sm font-semibold">Pokaż logo w stopce</div>
            <div className="text-xs text-slate-500">Logo osadzane bezpośrednio w wiadomości (widoczne nawet przy zablokowanych obrazkach).</div>
          </div>
          <Switch data-testid="smtp-signature-logo" checked={data.signature_logo} onCheckedChange={(v) => set("signature_logo", v)} />
        </div>
        <div>
          <Label className="text-xs">Treść stopki (każda linia osobno)</Label>
          <Textarea data-testid="smtp-signature" rows={4} value={data.signature} onChange={(e) => set("signature", e.target.value)}
            placeholder={"Twoje Biuro — wirtualne biuro i księgowość\nul. Domaniewska 17/19 lok. 133, 02-672 Warszawa\ntel. +48 22 544 68 84 · biuro@twojebiuro.waw.pl"} />
        </div>
      </div>

      <div className="bg-card border border-border rounded-2xl p-5 sm:p-6 space-y-4">
        <div>
          <h3 className="text-base font-semibold">Kopia do biura (UDW / BCC)</h3>
          <p className="text-sm text-slate-500 mt-1">Ukryta kopia każdego e-maila wysyłanego do klienta trafia na wskazany adres — archiwum korespondencji. Maile kierowane do biura nie są dublowane.</p>
        </div>
        <div className="flex items-center justify-between rounded-xl border border-border px-4 py-3">
          <div>
            <div className="text-sm font-semibold">Wysyłaj ukrytą kopię do biura</div>
            <div className="text-xs text-slate-500">Dotyczy e-maili do klientów (potwierdzenia, przypomnienia, dokumenty).</div>
          </div>
          <Switch data-testid="smtp-bcc-enabled" checked={data.bcc_enabled} onCheckedChange={(v) => set("bcc_enabled", v)} />
        </div>
        <div>
          <Label className="text-xs">Adres archiwum (puste = adres powiadomień biura)</Label>
          <Input data-testid="smtp-bcc" value={data.bcc} onChange={(e) => set("bcc", e.target.value)} placeholder="info@twojebiuro.waw.pl" />
        </div>
        <div className="flex flex-wrap items-center gap-3 pt-1">
          <button data-testid="smtp-save-btn2" onClick={save} disabled={!!busy}
            className={`${btn} bg-brand-teal text-white hover:bg-brand-tealDark`}>
            {busy === "save" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Zapisz ustawienia
          </button>
        </div>
      </div>

      <div className="bg-card border border-border rounded-2xl p-5 sm:p-6 space-y-3">
        <h3 className="text-sm font-semibold">Wyślij wiadomość testową</h3>
        <p className="text-xs text-slate-500">Zapisze bieżące dane i spróbuje wysłać testowego e-maila, aby sprawdzić poprawność konta.</p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <Label className="text-xs">Adres testowy (puste = adres nadawcy)</Label>
            <Input data-testid="smtp-test-to" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="twoj-adres@example.com" />
          </div>
          <button data-testid="smtp-test-btn" onClick={test} disabled={!!busy}
            className={`${btn} border border-brand-teal text-brand-teal hover:bg-brand-teal/10`}>
            {busy === "test" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Wyślij test
          </button>
        </div>
      </div>
    </div>
  );
}
